import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { RefreshCw, ExternalLink, Package, Mail, MapPin, CreditCard, AlertCircle, FileDown, Send, Truck, X, CheckSquare, Square, Download, Zap } from "lucide-react";
import { toast } from "sonner";
import Autocomplete from "@/components/Autocomplete";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAYMENT_STYLES = {
  paid: "bg-gold/20 text-gold border-gold/50",
  pending: "bg-white/10 text-white/60 border-white/15",
  failed: "bg-red-500/15 text-red-300 border-red-400/40",
};
const SINALITE_STYLES = {
  pending_artwork: "bg-purple/20 text-purple-hi border-purple-hi/40",
  ready_to_submit: "bg-blue-500/15 text-blue-300 border-blue-400/40",
  submitted: "bg-blue-500/15 text-blue-300 border-blue-400/40",
  shipped: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40",
  failed: "bg-red-500/15 text-red-300 border-red-400/40",
};

export default function AdminOrders({ password }) {
  const headers = useMemo(() => ({ "X-Admin-Password": password }), [password]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [shipModalOrder, setShipModalOrder] = useState(null);
  const [submittingSinalite, setSubmittingSinalite] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulking, setBulking] = useState(false);
  // Pagination
  const [page, setPage] = useState(0);
  const [totalOrders, setTotalOrders] = useState(0);
  const PAGE_SIZE = 25;

  // Settings — auto-submit to SinaLite toggle
  const [autoSubmit, setAutoSubmit] = useState(null); // null = loading
  const loadSettings = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/settings`, { headers });
      setAutoSubmit(Boolean(data.settings?.auto_submit_sinalite?.value));
    } catch { /* silent */ }
  }, [headers]);
  useEffect(() => { loadSettings(); }, [loadSettings]);

  const toggleAutoSubmit = async () => {
    const next = !autoSubmit;
    setAutoSubmit(next);
    try {
      await axios.patch(`${API}/settings/auto_submit_sinalite`, { value: next }, { headers });
      toast.success(next
        ? "Auto-submit ON — new orders will send to SinaLite automatically."
        : "Auto-submit OFF — you'll need to send orders manually from this page.");
    } catch (err) {
      setAutoSubmit(!next);
      toast.error(err.response?.data?.detail || "Could not update setting");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/checkout/admin/orders?limit=${PAGE_SIZE}&skip=${page * PAGE_SIZE}`,
        { headers },
      );
      setOrders(data.items || []);
      setTotalOrders(data.total || 0);
    } catch {
      toast.error("Failed to load orders");
    } finally {
      setLoading(false);
    }
  }, [headers, page]);

  useEffect(() => { load(); }, [load]);

  // Clear selection whenever the page changes or list reloads.
  useEffect(() => { setSelected(new Set()); }, [page]);

  const stats = useMemo(() => ({
    total: orders.length,
    paid: orders.filter((o) => o.payment_status === "paid").length,
    revenue: orders.filter((o) => o.payment_status === "paid").reduce((s, o) => s + (o.total_cents || 0), 0) / 100,
    pending_artwork: orders.filter((o) => o.sinalite_status === "pending_artwork" && o.payment_status === "paid").length,
  }), [orders]);

  const submitToSinalite = async (orderId) => {
    setSubmittingSinalite(orderId);
    try {
      await axios.post(`${API}/checkout/admin/orders/${orderId}/submit-to-sinalite`, {}, { headers });
      toast.success("Submitted to SinaLite");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Submit failed");
    } finally {
      setSubmittingSinalite(null);
    }
  };

  const onShipped = async () => {
    await load();
    setShipModalOrder(null);
  };

  const toggleOne = (id, e) => {
    e.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAllVisible = () => {
    setSelected((prev) => {
      const allIds = orders.map((o) => o.id);
      const allSelected = allIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(allIds);
    });
  };

  const clearSelection = () => setSelected(new Set());

  const bulkSetSinaliteStatus = async (status) => {
    if (selected.size === 0) return;
    setBulking(true);
    try {
      const { data } = await axios.post(
        `${API}/checkout/admin/orders/bulk-status`,
        { order_ids: Array.from(selected), sinalite_status: status },
        { headers },
      );
      toast.success(`Updated ${data.modified} of ${data.matched} orders → ${status.replace("_", " ")}`);
      clearSelection();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk update failed");
    } finally {
      setBulking(false);
    }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} order(s)? Orders already submitted to SinaLite will be skipped. This cannot be undone.`)) return;
    setBulking(true);
    try {
      const { data } = await axios.post(
        `${API}/checkout/admin/orders/bulk-delete`,
        { order_ids: Array.from(selected) },
        { headers },
      );
      toast.success(`Deleted ${data.deleted} order(s).`);
      clearSelection();
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Bulk delete failed");
    } finally {
      setBulking(false);
    }
  };

  const pageStart = page * PAGE_SIZE + (orders.length ? 1 : 0);
  const pageEnd = page * PAGE_SIZE + orders.length;
  const hasMore = pageEnd < totalOrders;

  // Autocomplete: search across ALL orders in the DB via the admin search endpoint.
  const searchOrders = useCallback(async (query) => {
    try {
      const { data } = await axios.get(`${API}/admin/search`, { params: { q: query, limit: 8 }, headers });
      return (data.results || []).filter((r) => r.type === "order");
    } catch {
      return [];
    }
  }, [headers]);

  const goToOrder = (item) => {
    // Expand the matching order card if it's in view; else jump to the page containing it.
    const found = orders.find((o) => o.id === item.id);
    if (found) {
      setExpanded(found.id);
      const el = document.querySelector(`[data-testid='order-card-${found.id}']`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      toast.message("Order not on this page — refreshing to locate it.");
      setPage(0);
      load();
    }
  };

  return (
    <div data-testid="admin-orders">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="eyebrow gold-bar">Print Shop</div>
          <h2 className="font-display text-3xl md:text-4xl mt-2 tracking-tight">Orders</h2>
          <p className="text-white/50 text-sm mt-2">
            {totalOrders} orders · {stats.paid} paid · ${stats.revenue.toFixed(2)} revenue · {stats.pending_artwork} awaiting artwork
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="flex items-center gap-3 px-3 py-2 border border-white/10 rounded-sm"
            data-testid="auto-submit-toggle-wrap"
            title={
              autoSubmit === null
                ? "Loading auto-submit setting…"
                : autoSubmit
                  ? "Orders auto-submit to SinaLite as soon as payment + artwork are received. Click to switch to manual."
                  : "Manual mode — you'll need to click 'Send to SinaLite' on each order. Click to enable auto-submit."
            }
          >
            <Zap size={13} className={autoSubmit ? "text-gold" : "text-white/40"} />
            <div className="text-[10px] uppercase tracking-[0.18em] text-white/70">
              Auto-submit
            </div>
            <button
              type="button"
              onClick={toggleAutoSubmit}
              disabled={autoSubmit === null}
              data-testid="auto-submit-toggle"
              aria-pressed={Boolean(autoSubmit)}
              className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors disabled:opacity-40 ${autoSubmit ? "bg-gold" : "bg-white/10"}`}
            >
              <span className={`inline-block h-3 w-3 rounded-full bg-white shadow transition-transform ${autoSubmit ? "translate-x-5" : "translate-x-1"}`} />
            </button>
          </div>
          <a
            href={`${API}/checkout/admin/orders.csv`}
            data-testid="orders-csv-btn"
            title="Export all orders as CSV — sends the admin password automatically via a signed URL is NOT used here, so the browser prompts if the header is missing. Use the Copy CSV command below if your browser doesn't include the header."
            className="btn-ghost !py-3 !px-4"
            onClick={async (e) => {
              // Anchor tags can't set the auth header — fetch the file with axios and trigger a download.
              e.preventDefault();
              try {
                const res = await axios.get(`${API}/checkout/admin/orders.csv`, { headers, responseType: "blob" });
                const url = window.URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
                const a = document.createElement("a");
                a.href = url;
                a.download = `tcsw-orders-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a); a.click(); a.remove();
                window.URL.revokeObjectURL(url);
                toast.success("Order history exported.");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Export failed");
              }
            }}
          >
            <Download size={14} /> Export CSV
          </a>
          <button onClick={load} data-testid="orders-refresh" className="btn-ghost !py-3 !px-4">
            <RefreshCw size={14} /> Reload
          </button>
        </div>
      </div>

      {/* Order search — autocomplete over ALL orders in the DB */}
      <div className="mb-4" data-testid="orders-search-wrap">
        <Autocomplete
          variant="admin"
          testId="orders-search"
          placeholder="Search orders — by ID, email, or customer name…"
          onQuery={searchOrders}
          onSelect={goToOrder}
          noResultsHint="No matching orders in the last search."
        />
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          className="mb-4 card-dark border-gold/40 bg-gold/5 flex flex-wrap items-center gap-3 p-3 sticky top-4 z-30"
          data-testid="bulk-action-bar"
        >
          <button
            onClick={clearSelection}
            data-testid="bulk-clear"
            className="text-white/60 hover:text-white p-1.5"
            aria-label="Clear selection"
          ><X size={14} /></button>
          <div className="text-[11px] uppercase tracking-[0.22em] text-gold font-semibold">
            {selected.size} selected
          </div>
          <div className="h-4 w-px bg-white/15" />
          <button
            onClick={() => bulkSetSinaliteStatus("cancelled")}
            disabled={bulking}
            data-testid="bulk-cancel"
            className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-white/15 text-white/70 hover:border-white/40 hover:text-white transition-colors disabled:opacity-40"
          >Mark cancelled</button>
          <button
            onClick={() => bulkSetSinaliteStatus("failed")}
            disabled={bulking}
            data-testid="bulk-failed"
            className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-red-400/40 text-red-300 hover:bg-red-500/10 transition-colors disabled:opacity-40"
          >Mark failed</button>
          <button
            onClick={() => bulkSetSinaliteStatus("ready_to_submit")}
            disabled={bulking}
            data-testid="bulk-ready"
            className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-blue-400/40 text-blue-300 hover:bg-blue-500/10 transition-colors disabled:opacity-40"
          >Reset to ready</button>
          <div className="h-4 w-px bg-white/15 ml-auto" />
          <button
            onClick={bulkDelete}
            disabled={bulking}
            data-testid="bulk-delete"
            className="text-[10px] uppercase tracking-[0.2em] px-3 py-1.5 border border-red-400/60 text-red-300 hover:bg-red-500 hover:text-white transition-colors disabled:opacity-40 flex items-center gap-2"
          ><X size={12} /> Delete selected</button>
        </div>
      )}

      {loading ? (
        <div className="card-dark p-16 text-center text-white/50">Loading orders…</div>
      ) : orders.length === 0 ? (
        <div className="card-dark p-16 text-center text-white/50" data-testid="orders-empty">
          <Package size={28} className="mx-auto text-gold/50 mb-4" />
          No orders yet — once customers check out, they&apos;ll appear here.
        </div>
      ) : (
        <>
        {/* Select-all row */}
        <div className="flex items-center gap-3 mb-3 px-1 text-[11px] uppercase tracking-[0.22em] text-white/50">
          <button
            onClick={toggleAllVisible}
            data-testid="orders-select-all"
            className="flex items-center gap-2 hover:text-gold transition-colors"
          >
            {orders.every((o) => selected.has(o.id)) && orders.length > 0
              ? <CheckSquare size={14} className="text-gold" />
              : <Square size={14} />}
            Select all on page
          </button>
          <span className="ml-auto">Showing {pageStart}–{pageEnd} of {totalOrders}</span>
        </div>
        <div className="space-y-3">
          {orders.map((o) => (
            <article
              key={o.id}
              className={`card-dark overflow-hidden ${selected.has(o.id) ? "border-gold/40 ring-1 ring-gold/30" : ""}`}
              data-testid={`order-card-${o.id}`}
            >
              <div className="flex">
                <button
                  onClick={(e) => toggleOne(o.id, e)}
                  data-testid={`order-select-${o.id}`}
                  aria-label={selected.has(o.id) ? "Deselect order" : "Select order"}
                  className="px-4 py-6 border-r border-white/5 hover:bg-white/[0.02] transition-colors flex items-start"
                >
                  {selected.has(o.id)
                    ? <CheckSquare size={16} className="text-gold" />
                    : <Square size={16} className="text-white/40 hover:text-gold" />}
                </button>
                <button
                  onClick={() => setExpanded(expanded === o.id ? null : o.id)}
                  className="flex-1 text-left p-6 hover:bg-white/[0.015] transition-colors"
                >
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto_auto] gap-4 items-center">
                  <div className="min-w-0">
                    <div className="font-display text-lg text-white truncate">{o.email}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1 font-mono">#{o.id.slice(0, 8)}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Total</div>
                    <div className="font-display text-xl text-gold">${((o.total_cents || 0) / 100).toFixed(2)}</div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border ${PAYMENT_STYLES[o.payment_status] || ""}`}>
                      {o.payment_status}
                    </span>
                    <span className={`text-[10px] uppercase tracking-[0.18em] px-2.5 py-1 border ${SINALITE_STYLES[o.sinalite_status] || ""}`}>
                      {(o.sinalite_status || "").replace("_", " ")}
                    </span>
                  </div>
                  <div className="text-right text-[10px] uppercase tracking-[0.2em] text-white/40">
                    {new Date(o.created_at).toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
                  </div>
                </div>
              </button>
              </div>

              {expanded === o.id && (
                <div className="border-t border-white/5 p-6 grid grid-cols-1 lg:grid-cols-3 gap-6 text-sm" data-testid={`order-details-${o.id}`}>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3 flex items-center gap-2"><Mail size={11} className="text-gold" /> Contact</div>
                    <div className="text-white/85">{o.email}</div>
                    <div className="text-white/60 mt-1">{o.shipping?.name}</div>
                    {o.shipping?.phone && <div className="text-white/60">{o.shipping.phone}</div>}
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3 flex items-center gap-2"><MapPin size={11} className="text-gold" /> Shipping</div>
                    <div className="text-white/85">{o.shipping?.street1}</div>
                    {o.shipping?.street2 && <div className="text-white/85">{o.shipping.street2}</div>}
                    <div className="text-white/60 mt-1">{o.shipping?.city}, {o.shipping?.state} {o.shipping?.postal_code}</div>
                    <div className="text-white/60">{o.shipping?.country}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3 flex items-center gap-2"><CreditCard size={11} className="text-gold" /> Payment</div>
                    <div className="text-white/60 text-xs font-mono break-all">{o.square_payment_id || "—"}</div>
                    {o.square_receipt_url && (
                      <a href={o.square_receipt_url} target="_blank" rel="noreferrer" className="text-gold text-xs hover:underline inline-flex items-center gap-1 mt-1">
                        View Square receipt <ExternalLink size={10} />
                      </a>
                    )}
                    {o.payment_error && (
                      <div className="mt-2 text-red-300 text-xs flex items-start gap-2">
                        <AlertCircle size={12} className="shrink-0 mt-0.5" />
                        {JSON.stringify(o.payment_error).slice(0, 200)}
                      </div>
                    )}
                  </div>

                  <div className="lg:col-span-3">
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">Line items</div>
                    <ul className="divide-y divide-white/5 border border-white/5 rounded-sm">
                      {(o.lines || []).map((l, i) => (
                        <li key={i} className="p-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
                          <div>
                            <div className="text-white">{l.product_name}</div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mt-1">
                              #{l.product_id} · {l.sku} · {Object.entries(l.selection || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")}
                            </div>
                          </div>
                          <div className="text-right whitespace-nowrap">
                            <div className="text-white/85">{l.quantity} × ${Number(l.unit_price).toFixed(2)}</div>
                            <div className="text-[10px] uppercase tracking-[0.16em] text-white/40 mt-1">
                              wholesale ${Number(l.wholesale_unit).toFixed(2)}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="lg:col-span-3 flex flex-wrap items-center gap-6 pt-4 border-t border-white/5 text-sm">
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 mr-2">Subtotal</span>
                      <span className="text-white/85">${((o.subtotal_cents || 0) / 100).toFixed(2)}</span>
                    </div>
                    {(o.discount_cents || 0) > 0 && (
                      <div>
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 mr-2">Discount{o.coupon_code ? ` (${o.coupon_code})` : ""}</span>
                        <span className="text-gold">− ${((o.discount_cents || 0) / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {(o.shipping_cents || 0) > 0 && (
                      <div>
                        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 mr-2">Shipping{o.shipping_method ? ` · ${o.shipping_method}` : ""}</span>
                        <span className="text-white/85">${((o.shipping_cents || 0) / 100).toFixed(2)}</span>
                      </div>
                    )}
                    <div>
                      <span className="text-[10px] uppercase tracking-[0.22em] text-white/40 mr-2">Total</span>
                      <span className="text-gold font-display text-lg">${((o.total_cents || 0) / 100).toFixed(2)}</span>
                    </div>
                    {o.artwork_uploaded && (
                      <ArtworkDownloadBtn
                        orderId={o.id}
                        filename={o.artwork_filename}
                        size={o.artwork_size}
                        password={password}
                      />
                    )}
                    {o.note && (
                      <div className="text-white/60 text-xs">Note: {o.note}</div>
                    )}
                  </div>

                  {/* Fulfillment actions */}
                  {o.payment_status === "paid" && (
                    <div className="lg:col-span-3 flex flex-wrap items-center gap-3 pt-4 border-t border-white/5">
                      <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mr-2 flex items-center gap-2">
                        <Truck size={11} className="text-gold" /> Fulfillment
                      </div>
                      {o.sinalite_order_id ? (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-emerald-300 border border-emerald-400/40 bg-emerald-500/10 px-3 py-1.5">
                          SinaLite #{o.sinalite_order_id}
                        </span>
                      ) : o.artwork_uploaded ? (
                        <button
                          onClick={() => submitToSinalite(o.id)}
                          disabled={submittingSinalite === o.id}
                          data-testid={`submit-sinalite-${o.id}`}
                          className="text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-purple-hi/60 text-purple-hi hover:bg-purple-hi hover:text-black transition-colors flex items-center gap-2 disabled:opacity-40"
                        >
                          <Send size={11} /> {submittingSinalite === o.id ? "Submitting…" : "Submit to SinaLite"}
                        </button>
                      ) : (
                        <span className="text-[10px] uppercase tracking-[0.2em] text-white/40">Awaiting artwork</span>
                      )}
                      {o.shipped_at ? (
                        <div className="flex items-center gap-2 text-[11px] text-emerald-300" data-testid={`shipped-info-${o.id}`}>
                          <span className="uppercase tracking-[0.2em] text-[10px]">Shipped:</span>
                          {o.tracking_carrier} · <span className="font-mono">{o.tracking_number}</span>
                          {o.tracking_url && (
                            <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-gold hover:underline inline-flex items-center gap-1">
                              track <ExternalLink size={10} />
                            </a>
                          )}
                        </div>
                      ) : (
                        <button
                          onClick={() => setShipModalOrder(o)}
                          data-testid={`mark-shipped-btn-${o.id}`}
                          className="ml-auto text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-gold text-gold hover:bg-gold hover:text-black transition-colors flex items-center gap-2"
                        >
                          <Truck size={11} /> Mark as shipped
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </article>
          ))}
        </div>

        {/* Pagination */}
        {(page > 0 || hasMore) && (
          <div className="mt-6 flex items-center justify-between text-[11px] uppercase tracking-[0.22em] text-white/50" data-testid="orders-pagination">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              data-testid="orders-prev-page"
              className="btn-ghost !py-2 !px-4 disabled:opacity-30 disabled:cursor-not-allowed"
            >← Previous</button>
            <div>Page {page + 1} of {Math.max(1, Math.ceil(totalOrders / PAGE_SIZE))}</div>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={!hasMore}
              data-testid="orders-next-page"
              className="btn-ghost !py-2 !px-4 disabled:opacity-30 disabled:cursor-not-allowed"
            >Next →</button>
          </div>
        )}
        </>
      )}

      {shipModalOrder && (
        <MarkShippedModal
          order={shipModalOrder}
          password={password}
          onClose={() => setShipModalOrder(null)}
          onDone={onShipped}
        />
      )}
    </div>
  );
}


function MarkShippedModal({ order, password, onClose, onDone }) {
  const [carrier, setCarrier] = useState("Canada Post");
  const [tracking, setTracking] = useState("");
  const [url, setUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!tracking.trim() || tracking.trim().length < 3) {
      toast.error("Enter a tracking number (min 3 chars).");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/checkout/admin/orders/${order.id}/mark-shipped`,
        {
          tracking_number: tracking.trim(),
          tracking_carrier: carrier.trim(),
          tracking_url: url.trim() || null,
        },
        { headers: { "X-Admin-Password": password } },
      );
      toast.success("Order marked as shipped. Buyer notified by email.");
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not mark as shipped");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="mark-shipped-modal"
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="w-full max-w-md card-dark p-8 relative"
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 text-white/50 hover:text-white"
          aria-label="Close"
          data-testid="mark-shipped-close"
        ><X size={16} /></button>

        <div className="eyebrow gold-bar">Fulfillment</div>
        <h2 className="font-display text-2xl mt-2 tracking-tight">Mark as shipped</h2>
        <p className="text-white/50 text-xs mt-2">Order #{order.id.slice(0, 8)} · {order.email}</p>

        <label className="block mt-6">
          <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Carrier</span>
          <select
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            data-testid="ship-carrier"
            className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
          >
            {["Canada Post", "UPS", "FedEx", "Purolator", "DHL", "Other"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>

        <label className="block mt-4">
          <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Tracking number <span className="text-gold">*</span></span>
          <input
            type="text"
            value={tracking}
            onChange={(e) => setTracking(e.target.value)}
            required
            data-testid="ship-tracking"
            placeholder="1Z999AA10123456784"
            className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold font-mono"
          />
        </label>

        <label className="block mt-4">
          <span className="block text-[10px] uppercase tracking-[0.22em] text-white/55 mb-2">Tracking URL (optional)</span>
          <input
            type="url"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            data-testid="ship-url"
            placeholder="https://…"
            className="w-full bg-ink-800 border border-white/10 text-white px-4 py-3 text-sm rounded-sm outline-none focus:border-gold"
          />
        </label>

        <div className="mt-7 flex items-center gap-3">
          <button
            type="submit"
            disabled={submitting}
            data-testid="ship-submit"
            className="btn-gold flex-1 justify-center disabled:opacity-40"
          >
            {submitting ? "Sending…" : "Mark shipped & notify"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost !py-3 !px-4"
          >Cancel</button>
        </div>

        <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-5 text-center">
          Sends shipping confirmation email to buyer
        </p>
      </form>
    </div>
  );
}


function ArtworkDownloadBtn({ orderId, filename, size, password }) {
  const [downloading, setDownloading] = useState(false);

  const download = async () => {
    setDownloading(true);
    try {
      const url = `${API}/checkout/admin/orders/${orderId}/artwork`;
      const res = await fetch(url, { headers: { "X-Admin-Password": password } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = filename || `artwork-${orderId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      toast.error("Could not download artwork");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={download}
      disabled={downloading}
      data-testid={`download-artwork-${orderId}`}
      className="ml-auto text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-gold text-gold hover:bg-gold hover:text-black transition-colors flex items-center gap-2"
    >
      <FileDown size={12} />
      {downloading ? "Downloading…" : (
        <>Artwork <span className="text-[9px] opacity-70">· {((size || 0) / 1024 / 1024).toFixed(1)} MB</span></>
      )}
    </button>
  );
}
